import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../services/api";
import { getTimeZoneOptions, detectUserTimezone } from "../../utils/timezone";

function Register() {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    fullName: "",
    companyName: "",
    timezone: detectUserTimezone(),
  });

  const [verification, setVerification] = useState(false);
  const [code, setCode] = useState("");
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const { register, login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...registerData } = formData;

      await api.register(registerData);

      // Registration succeeded and verification code was sent.
      setVerification(true);
    } catch (err) {
      console.error("Registration error:", err);
      setError(
        err.response?.data?.error || "Registration failed. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const verifyRegistration = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const result = await api.verifyRegistration(formData.email, code);

      localStorage.setItem("accessToken", result.accessToken);
      localStorage.setItem("refreshToken", result.refreshToken);

      await login(formData.email, formData.password);

      navigate("/dashboard", { replace: true });
    } catch (err) {
      console.error("Verification error:", err);
      setError(
        err.response?.data?.error || "Verification failed. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    setError("");
    setResending(true);

    try {
      await api.resendRegistrationCode(formData.email);
    } catch (err) {
      console.error("Resend error:", err);
      setError(
        err.response?.data?.error || "Unable to resend verification code.",
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="logo">
          <i className="fas fa-chart-line"></i>
          <h1>CRM</h1>
          <span className="badge">Enterprise</span>
        </div>

        {!verification ? (
          <>
            <h2>Create Account</h2>
            <p className="subtitle">Register to get started</p>

            {error && (
              <div className="error-message">
                <i className="fas fa-exclamation-circle"></i>
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  name="fullName"
                  className="form-control"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="John Doe"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Company Name</label>
                <input
                  type="text"
                  name="companyName"
                  className="form-control"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder="Your Company"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  name="password"
                  className="form-control"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Min 8 characters"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Confirm Password *</label>
                <input
                  type="password"
                  name="confirmPassword"
                  className="form-control"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Time Zone</label>
                <select
                  name="timezone"
                  className="form-control"
                  value={formData.timezone}
                  onChange={handleChange}
                  disabled={loading}
                >
                  {getTimeZoneOptions().map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>

                <small className="text-muted">
                  Your local time zone for displaying dates and times
                </small>
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-block"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Creating
                    account...
                  </>
                ) : (
                  "Create Account"
                )}
              </button>
            </form>

            <div className="login-footer">
              <p>
                Already have an account? <Link to="/login">Sign in</Link>
              </p>
            </div>
          </>
        ) : (
          <>
            <h2>Verify Email</h2>

            <p className="subtitle">
              Enter the 6 digit code sent to {formData.email}
            </p>

            {error && (
              <div className="error-message">
                <i className="fas fa-exclamation-circle"></i>
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={verifyRegistration}>
              <div className="form-group">
                <label>Verification Code *</label>

                <input
                  type="text"
                  className="form-control"
                  inputMode="numeric"
                  maxLength="6"
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  placeholder="Enter 6-digit code"
                  required
                  autoFocus
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-block"
                disabled={loading || code.length !== 6}
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Verifying...
                  </>
                ) : (
                  "Verify & Create Account"
                )}
              </button>
            </form>

            <button
              type="button"
              className="btn btn-secondary btn-block"
              onClick={resendCode}
              disabled={resending || loading}
            >
              {resending ? "Sending..." : "Resend Code"}
            </button>

            <div className="login-footer">
              <p>
                Already have an account? <Link to="/login">Sign in</Link>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Register;
